/* eslint-disable @typescript-eslint/naming-convention */
import React from 'react'
import LiveReceptionistIcon from '../icons/eSIM.png'
import IntelligentAnswerBotIcon from '../icons/eSIM.png'
import ZohoIcon from '../icons/eSIM.png'
import DevicesIcon from '../icons/eSIM.png'
import NumberManagerIcon from '../icons/eSIM.png'
import { log } from './utils'
import { IconNameType } from 'foundations-library/types'

interface Media {
    type: 'picture'|'video',
    sources: React.SourceHTMLAttributes<HTMLSourceElement>[],
    thumbnail?: string
}

interface Tag {
    text: string,
    icon?: IconNameType,
    color?: string,
}

interface Feature { name: string, link?: string, icon?: IconNameType }

type OverviewGeneral = { media: Media, info: string }
type OverviewTab = { heading: string, general: OverviewGeneral[] }
type FAQ = { question: string, answer: string }
type FAQTab = FAQ[]
type HowToVideo = (Required<Omit<Media, 'type'>> & { info: string })
type HowToArticle = { title: string }
type HowToArticleData = { media: Media, info: { title: string, description?: string } }
type HowToTab = { videos: HowToVideo[], articles: HowToArticleData[] }

/**
 *
 */
type AppHeader = {
    title: string,
    tags?: Tag[],
    features?: Feature[],
    image?: Media,
    backgroundColor?: string
}

interface MarkdownTab {
    name: string,
    type?: 'markdown',
    searchable?: boolean,
    styleOverrides?: Record<string, any>,
    content: string
}

interface ComponentTab {
    name: string,
    type: 'component',
    content: {
        componentName: 'overview' | 'faq' | 'howTo',
        data: OverviewTab | FAQTab | HowToTab
    }
}

interface AppDetails {
    description: string,
    header?: AppHeader,
    tabs?: (MarkdownTab | ComponentTab)[]
}

enum AppType {
    Modal = 'MODAL',
    Integrated = 'INTEGRATED',
    Internal = 'INTERNAL',
    External = 'EXTERNAL'
}

interface UserTypes {
    nxt: {
        admin: boolean,
        nonAdmin: boolean
    },
    classic: {
        admin: boolean,
        nonAdmin: boolean
    }
}

interface Button {
    text: string,
    style: 'primary' | 'secondary',
    visibility: UserTypes,
    action: {
        type: AppType,
        modal?: string
        url?: string
    }
}

interface App {
    id: number,
    title: string,
    company: string,
    tag?: string,
    icon: string,
    inlineIcon?: string,
    learnMoreText: string,
    learnMoreLink: string,
    details: AppDetails,
    userTypes: UserTypes,
    url?: string,
    appType?: AppType
    buttons?: Button[],
    identifier?: string
}

interface RouterProps {
    history: Record<string, any>
    location: Record<string, any>
    match: Record<string, any>
    staticContext?: any
}

const media: Media[] = [
    {
        type: 'picture',
        sources: [
            {
                src: 'https://www.ringcentral.com/us/en/blog/wp-content/uploads/2021/07/Phone-blog.png',
                type: 'image/png'
            }
        ]
    },
    {
        type: 'picture',
        sources: [
            {
                src: 'https://www.linguahouse.com/linguafiles/md5/032c7282f877b86c74e0a97d0b8bb7e8',
                type: 'image/jpg'
            }
        ]
    },
    {
        type: 'video',
        sources: [
            {
                src: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerBlazes.mp4',
                type: 'video/mp4'
            }
        ]
    },
    {
        type: 'video',
        sources: [
            {
                src: 'https://commondatastorage.googleapis.com/gtv-videos-bucket/sample/ForBiggerFun.mp4',
                type: 'video/mp4'
            }
        ]
    }
]

const LiveReceptionist: App = {
    id: 1,
    title: 'Live Receptionist',
    company: 'Phone.com',
    icon: LiveReceptionistIcon,
    learnMoreText: 'Learn more',
    learnMoreLink: '/live-receptionist/create/8',
    details: {
        description: 'This is description',
        header: { title: 'Elevate your customer experience with Live Receptionist' },
        tabs: [
            {
                name: 'overview',
                type: 'component',
                content: { componentName: 'overview', data: { heading: 'Overview', general: media.map(m => ({ media: m, info: 'This is description' })) } }
            },
            {
                name: 'faq',
                type: 'component',
                content: { componentName: 'faq', data: [{ question: 'What is Live Receptionist?', answer: 'Live Receptionist is a feature that allows you to receive calls and messages on your phone.' }] }
            }
        ]
    },
    userTypes: {
        nxt: {
            admin: true,
            nonAdmin: true
        },
        classic: {
            admin: true,
            nonAdmin: true
        }
    },
    url: '/live-receptionist/create/8',
    identifier: 'Receptionist'
}

const IntelligentAnswerBot: App = {
    id: 2,
    title: 'AI-Connect',
    company: 'Phone.com',
    icon: IntelligentAnswerBotIcon,
    learnMoreText: 'Learn more',
    learnMoreLink: 'https://www.phone.com/splash-pages/add-intelligent-answer-bot/',
    details: {
        description: 'Phone.com\'s AI-powered Intelligent Answer Bot routes callers according to your instructions 24*7 so that you can connect your callers with the right person. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.'
    },
    userTypes: {
        nxt: {
            admin: true,
            nonAdmin: true
        },
        classic: {
            admin: true,
            nonAdmin: true
        }
    },
    url: '/live-receptionist/create/4'
}

const ZohoCRM: App = {
    id: 3,
    title: 'Zoho CRM',
    company: 'Zoho, Inc.',
    icon: ZohoIcon,
    learnMoreText: 'Learn more',
    learnMoreLink: '',
    details: {
        description: 'Zoho CRM for Phone.com brings integrated cloud business communications and CRM together, for users to manage all the call related activities inside CRM. Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.'
    },
    userTypes: {
        nxt: {
            admin: true,
            nonAdmin: true
        },
        classic: {
            admin: true,
            nonAdmin: true
        }
    },
    url: 'https://marketplace.zoho.com/app/crm/phone-com-for-zoho-crm',
    appType: AppType.Integrated
}

const Devices: App = {
    id: 4,
    title: 'Devices',
    company: 'Phone.com',
    icon: DevicesIcon,
    learnMoreText: 'Learn more',
    learnMoreLink: '/phones/add-phones',
    details: {
        description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.'
    },
    userTypes: {
        nxt: {
            admin: true,
            nonAdmin: true
        },
        classic: {
            admin: true,
            nonAdmin: true
        }
    },
    url: '/phones/add-phones'
}

const NumberManager: App = {
    id: 5,
    title: 'Number Manager',
    company: 'Phone.com',
    icon: NumberManagerIcon,
    learnMoreText: 'Learn more',
    learnMoreLink: '/phone-numbers/add-numbers/vanity',
    details: {
        description: 'Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat. Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur. Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.'
    },
    userTypes: {
        nxt: {
            admin: true,
            nonAdmin: true
        },
        classic: {
            admin: true,
            nonAdmin: true
        }
    },
    url: '/phone-numbers/add-numbers/vanity'
}

const apps: App[] = [
    LiveReceptionist,
    IntelligentAnswerBot,
    ZohoCRM,
    Devices,
    NumberManager
]

const installedAppOptions = [
    {
        text: 'Uninstall',
        onClick: () => { log('Uninstall') },
        disabled: false,
        tooltipText: null
    },
    {
        text: 'Open',
        onClick: () => { log('Open') },
        disabled: false,
        tooltipText: null
    }
]
const installedAppStatus = [
    {
        content: 'Active',
        value: 1
    },
    {
        content: 'Inactive',
        value: 0
    }
]

/**
 *
 */
export const User = {
    user_id: 1,
    first_name: 'Pro',
    last_name: 'User',
    email: 'proUser@phone.com',
    role: 'account',
    user_account_type: 'pro',
    stage: 'PROD',
    v4ApiRoot: 'https://api.phone.com/v4',
    v5ApiRoot: 'https://preprod.my.phone.com/api/services',
    v5ToolsRoot: 'https://preprod.my.phone.com/api/tools',
    cp_token: 'abcdef',
    ac_token: 'abcdefgh',
    company: 'United People',
    extension: 601,
    features: new Set([]),
    extensions: [],
    true_account_id: 1857254,
    user_default_extension_id: 4027332,
    voip_id: '5464474',
    voip_phone_id: 4027553,
    user_tiered: true
}

/**
 *
 */
export { AppType, apps, installedAppOptions, installedAppStatus, media }
/**
 *
 */
export type { App, OverviewGeneral, FAQ, HowToVideo, HowToArticle, HowToArticleData, OverviewTab, FAQTab, HowToTab, MarkdownTab, ComponentTab, RouterProps, Button, UserTypes, Media, Feature, AppHeader }
